/**
 * batch-67 campagnes (FO-501) — bijtellingskeuze-campagne: gedeelde enums,
 * request-schema's (API `.safeParse` + inline veldfouten in de portal) en
 * DTO's voor portal (eigen keuze) en backoffice (beoordeling + opvolging).
 *
 * Regels (spec §A2, §5):
 *   - BIJTELLING = de auto wordt ook prive gebruikt (bijtelling van toepassing).
 *   - "Geen bijtelling" heeft twee vormen: GEEN_PRIVEGEBRUIK (verklaring geen
 *     privegebruik, geel kenteken) en UITSLUITEND_ZAKELIJK (bestelauto, grijs
 *     kenteken). Bij een GRIJS kenteken is UITSLUITEND_ZAKELIJK de enige
 *     "nee"-optie; bij geel zijn beide mogelijk.
 *   - Bij "nee" is het kilometerregistratie-akkoord verplicht en, voor de
 *     medewerker zelf, de handtekening (HR-namens/bevestiging na de deadline
 *     mag zonder handtekening, zoals de bestaande verklaring).
 */
import { z } from 'zod'
import { CAMPAGNE_DEELNAME_STATUS_LABELS, type CampagneDeelnameStatus } from './campagne'
import type { VoertuigGebruikType } from './voertuig-verklaring'

/**
 * Statuslabels in de bijtellingscampagne. De spec en de knoppen spreken van
 * "afkeuren"; de gedeelde enumwaarde heet AFGEWEZEN (zo heette hij al in de
 * vakantiecampagne). Een bijtellingsscherm toont dus "Afgekeurd", zodat de knop
 * en de status hetzelfde woord gebruiken.
 */
export const BIJTELLING_DEELNAME_STATUS_LABELS: Record<CampagneDeelnameStatus, string> = {
  ...CAMPAGNE_DEELNAME_STATUS_LABELS,
  AFGEWEZEN: 'Afgekeurd',
}

export const BIJTELLING_KEUZE_TYPE = ['BIJTELLING', 'GEEN_PRIVEGEBRUIK', 'UITSLUITEND_ZAKELIJK'] as const
export type BijtellingKeuzeType = (typeof BIJTELLING_KEUZE_TYPE)[number]

export const BIJTELLING_KEUZE_LABELS: Record<BijtellingKeuzeType, string> = {
  BIJTELLING: 'Bijtelling van toepassing (ook privégebruik)',
  GEEN_PRIVEGEBRUIK: 'Geen bijtelling: verklaring geen privégebruik (geel kenteken)',
  UITSLUITEND_ZAKELIJK: 'Geen bijtelling: uitsluitend zakelijk gebruik (grijs kenteken)',
}

export const BIJTELLING_KEUZE_KORT: Record<BijtellingKeuzeType, string> = {
  BIJTELLING: 'Bijtelling',
  GEEN_PRIVEGEBRUIK: 'Geen privégebruik',
  UITSLUITEND_ZAKELIJK: 'Uitsluitend zakelijk',
}

export const BIJTELLING_KEUZE_UITLEG: Record<BijtellingKeuzeType, string> = {
  BIJTELLING:
    'Je gebruikt de auto ook privé. De fiscale bijtelling wordt op je loon toegepast vanaf de ingangsdatum van deze keuze.',
  GEEN_PRIVEGEBRUIK:
    'Je rijdt per jaar maximaal 500 kilometer privé en houdt een sluitende rittenregistratie bij. STREEP dient de verklaring voor je in bij de Belastingdienst.',
  UITSLUITEND_ZAKELIJK:
    'De bestelauto wordt uitsluitend zakelijk gebruikt (ook geen woon-werkverkeer buiten de regels). Je houdt een rittenregistratie bij.',
}

/** Kentekenkleur zoals op het voertuig geregistreerd (vrij veld, genormaliseerd). */
export type KentekenKleur = 'GEEL' | 'GRIJS'

/**
 * Het veld op het voertuig is vrije tekst. Herkend wordt elke schrijfwijze waarin
 * de kleur als woord voorkomt ("Grijs", "grijs kenteken", "GRIJS (bestel)"): bij
 * een exacte vergelijking viel "grijs kenteken" terug op onbekend, en onbekend
 * biedt ook de verklaring voor een geel kenteken aan. Staan beide woorden erin,
 * of geen van beide, dan is de kleur onbekend (`null`): het formulier biedt dan
 * alle keuzes en de beoordelaar ziet "kentekenkleur onbekend".
 */
export function normaliseerKentekenKleur(raw: string | null | undefined): KentekenKleur | null {
  const v = (raw ?? '').trim().toUpperCase()
  const grijs = v.includes('GRIJS')
  const geel = v.includes('GEEL')
  if (grijs && !geel) return 'GRIJS'
  if (geel && !grijs) return 'GEEL'
  return null
}

/** De keuzes die bij deze kentekenkleur zijn toegestaan (spec §5.2). */
export function toegestaneBijtellingKeuzes(kleur: KentekenKleur | null): BijtellingKeuzeType[] {
  return kleur === 'GRIJS'
    ? ['BIJTELLING', 'UITSLUITEND_ZAKELIJK']
    : ['BIJTELLING', 'GEEN_PRIVEGEBRUIK', 'UITSLUITEND_ZAKELIJK']
}

/** Het bestaande verklaringstype dat bij een keuze hoort (FO-113-koppeling). */
export function keuzeNaarGebruikType(keuze: BijtellingKeuzeType): VoertuigGebruikType {
  return keuze === 'BIJTELLING' ? 'OOK_PRIVE' : 'UITSLUITEND_ZAKELIJK'
}

/** Bijtelling ja/nee uit de keuze. */
export function keuzeHeeftBijtelling(keuze: BijtellingKeuzeType): boolean {
  return keuze === 'BIJTELLING'
}

/**
 * "Keuze ongewijzigd t.o.v. vorig jaar" (spec §5.3): dezelfde bijtelling-uitkomst
 * als de lopende verklaring. Zonder lopende verklaring (nieuwe berijder) telt
 * elke keuze als gewijzigd (dan moet de salarisverwerker het sowieso weten).
 */
export function isKeuzeGewijzigd(keuze: BijtellingKeuzeType, bijtellingVorigJaar: boolean | null): boolean {
  if (bijtellingVorigJaar === null) return true
  return keuzeHeeftBijtelling(keuze) !== bijtellingVorigJaar
}

// ─── Request-schema's ────────────────────────────────────────────────────────

const handtekeningSchema = z
  .string()
  .trim()
  .max(3_000_000)
  .optional()
  .nullable()
  .transform((v) => (v == null || v.trim() === '' ? null : v))

/** Indienen door de medewerker (portal). */
export const bijtellingKeuzeIndienenSchema = z
  .object({
    keuze: z.enum(BIJTELLING_KEUZE_TYPE),
    kmRegistratieAkkoord: z.boolean().default(false),
    akkoord: z.boolean().refine((v) => v === true, { message: 'Bevestig dat je de keuze naar waarheid doorgeeft' }),
    handtekening: handtekeningSchema,
    opmerking: z.string().trim().max(500).optional().nullable().transform((v) => (v ? v : null)),
  })
  .superRefine((d, ctx) => {
    if (d.keuze !== 'BIJTELLING' && !d.kmRegistratieAkkoord) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['kmRegistratieAkkoord'],
        message: 'Zonder bijtelling is een akkoord op de kilometerregistratie verplicht',
      })
    }
  })
export type BijtellingKeuzeIndienenInput = z.infer<typeof bijtellingKeuzeIndienenSchema>

/**
 * Beoordelen (backoffice). Bij goedkeuren stuurt het scherm de keuze mee die de beoordelaar ZAG
 * (`keuze`): wijzigt de berijder zijn keuze terwijl de lijst openstaat, dan keurt de klik niet
 * stil de nieuwe keuze goed maar volgt een 409 met de vraag de pagina te vernieuwen.
 */
export const bijtellingKeuzeBeoordelenSchema = z
  .object({
    besluit: z.enum(['GOEDGEKEURD', 'AFGEWEZEN']),
    reden: z.string().trim().max(1000).optional().nullable().transform((v) => (v ? v : null)),
    keuze: z.enum(BIJTELLING_KEUZE_TYPE).optional(),
  })
  .superRefine((d, ctx) => {
    if (d.besluit === 'AFGEWEZEN' && !d.reden) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reden'], message: 'Geef een reden bij afkeuren' })
    }
    if (d.besluit === 'GOEDGEKEURD' && !d.keuze) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['keuze'], message: 'Geef mee welke keuze je goedkeurt' })
    }
  })
export type BijtellingKeuzeBeoordelenInput = z.infer<typeof bijtellingKeuzeBeoordelenSchema>

/**
 * HR bevestigt na de deadline de standaardkeuze "bijtelling van toepassing" namens
 * een berijder die niets doorgaf (FO-501 §A3: nooit stil, altijd met reden). Alleen
 * DIE keuze: een keuze zonder bijtelling is een verklaring die de medewerker zelf
 * ondertekent, met zijn akkoord op de kilometerregistratie. Daarvoor geeft HR hem
 * een nieuwe termijn (`bijtellingNieuweTermijnSchema`).
 */
export const bijtellingDefaultBevestigenSchema = z
  .object({
    keuze: z
      .enum(BIJTELLING_KEUZE_TYPE)
      .default('BIJTELLING')
      .refine((k) => k === 'BIJTELLING', {
        message:
          'Na de deadline bevestigt HR alleen "bijtelling van toepassing". Voor een keuze zonder bijtelling tekent de medewerker zelf: geef hem een nieuwe termijn.',
      }),
    reden: z.string().trim().min(3, 'Geef een reden op').max(1000),
  })
  .strict()
export type BijtellingDefaultBevestigenInput = z.infer<typeof bijtellingDefaultBevestigenSchema>

/**
 * HR geeft een berijder die zijn deadline miste een nieuwe termijn, zodat hij
 * alsnog zelf indient (en bij "geen bijtelling" zelf tekent). De termijn loopt tot
 * het einde van de NL-dag, het gekozen aantal kalenderdagen later.
 */
export const bijtellingNieuweTermijnSchema = z
  .object({
    dagen: z
      .number({ message: 'Kies een aantal dagen' })
      .int('Kies een heel aantal dagen')
      .min(1, 'Minstens 1 dag')
      .max(30, 'Hoogstens 30 dagen')
      .default(7),
  })
  .strict()
export type BijtellingNieuweTermijnInput = z.infer<typeof bijtellingNieuweTermijnSchema>

/** Afvinken "ingediend bij de Belastingdienst" / "NAW bevestigd" met datum. */
export const bijtellingOpvolgingAfvinkenSchema = z
  .object({
    stap: z.enum(['BELASTINGDIENST_INGEDIEND', 'NAW_BEVESTIGD']),
    /** ISO-datum (yyyy-mm-dd); leeg = vandaag. */
    datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  })
  .strict()
export type BijtellingOpvolgingAfvinkenInput = z.infer<typeof bijtellingOpvolgingAfvinkenSchema>

/**
 * De velden van een bijtellingscampagne, ZONDER kruiscontrole. Het aanmaak- en
 * het wijzig-schema bouwen hierop: Zod 4 weigert `.partial()` op een schema met
 * een verfijning (runtime-fout), dus de verfijning staat er per schema achter.
 */
const bijtellingCampagneBasis = z.object({
  naam: z.string().trim().min(1, 'Vul een naam in').max(200),
  jaar: z.number().int().min(2024).max(2100),
  /** ISO-instant van het startmoment (kaart + push). */
  startDatum: z.string().datetime(),
  deadline: z.string().datetime(),
  entityIds: z.array(z.string()).default([]),
  departmentIds: z.array(z.string()).default([]),
})

export const BIJTELLING_DEADLINE_NA_START = 'Deadline moet na de startdatum liggen'

/** Aanmaken van een bijtellingscampagne (backoffice, generiek). */
export const bijtellingCampagneSchema = bijtellingCampagneBasis.refine(
  (d) => new Date(d.deadline) > new Date(d.startDatum),
  { message: BIJTELLING_DEADLINE_NA_START, path: ['deadline'] },
)
export type BijtellingCampagneInput = z.infer<typeof bijtellingCampagneSchema>

/**
 * Wijzigen: elk veld optioneel, alleen wat meekomt verandert. Komen start en
 * deadline allebei mee, dan geldt de kruiscontrole hier; komt er maar een mee,
 * dan toetst de API hem tegen de opgeslagen waarde van de andere.
 */
export const bijtellingCampagneUpdateSchema = z
  .object({
    naam: bijtellingCampagneBasis.shape.naam.optional(),
    jaar: bijtellingCampagneBasis.shape.jaar.optional(),
    startDatum: bijtellingCampagneBasis.shape.startDatum.optional(),
    deadline: bijtellingCampagneBasis.shape.deadline.optional(),
    entityIds: z.array(z.string()).optional(),
    departmentIds: z.array(z.string()).optional(),
  })
  .refine((d) => !(d.startDatum && d.deadline) || new Date(d.deadline) > new Date(d.startDatum), {
    message: BIJTELLING_DEADLINE_NA_START,
    path: ['deadline'],
  })
export type BijtellingCampagneUpdateInput = z.infer<typeof bijtellingCampagneUpdateSchema>

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Portal: de eigen stand in een bijtellingscampagne (`GET /campagnes/bijtelling/:id/mijn`). */
export interface MijnBijtellingCampagneDTO {
  campagneId: string
  naam: string
  jaar: number
  status: 'CONCEPT' | 'OPEN' | 'GESLOTEN' | 'AFGEROND'
  /** Eigen deadline (ISO; eventueel de verlengde deadline van een late instromer). */
  deadline: string
  dagenResterend: number
  /** Ingangs- en einddatum van de nieuwe verklaring (ISO). */
  geldigVanaf: string
  geldigTot: string
  deelnameId: string | null
  deelnameStatus: CampagneDeelnameStatus | null
  /** Het voertuig waarop de keuze slaat (null = geen vaste toewijzing meer). */
  voertuig: {
    toewijzingId: string
    voertuigId: string
    kenteken: string
    merk: string
    model: string
    kentekenKleur: KentekenKleur | null
  } | null
  toegestaneKeuzes: BijtellingKeuzeType[]
  /** Bijtelling ja/nee volgens de lopende verklaring (null = geen lopende verklaring). */
  bijtellingVorigJaar: boolean | null
  /** Eerder ingediende keuze (bewerkbaar tot de beoordeling). */
  keuze: {
    keuze: BijtellingKeuzeType
    kmRegistratieAkkoord: boolean
    opmerking: string | null
    ingediendOp: string | null
  } | null
  /** Beoordeling (na indienen). */
  beoordeling: {
    status: CampagneDeelnameStatus
    opmerking: string | null
    beoordeeldOp: string | null
    herkansingTot: string | null
  } | null
  /** Blokkade: een andere medewerker heeft op dit voertuig nog een actieve verklaring voor het jaar. */
  blokkade: { reden: string } | null
  /**
   * Mag de medewerker nu (opnieuw) indienen? De API bepaalt dit met dezelfde regel
   * waarmee hij een inzending weigert; het formulier leidt het niet zelf af.
   */
  magIndienen: boolean
  /** Indienen kan dankzij een herkansing na een afkeuring (tot `beoordeling.herkansingTot`). */
  herkansing: boolean
}

/** Backoffice: een deelname in de beoordelingslijst. */
export interface BijtellingDeelnameRijDTO {
  deelnameId: string
  employeeId: string
  employeeNaam: string
  entityNaam: string | null
  departmentNaam: string | null
  status: CampagneDeelnameStatus
  deadline: string
  ingediendOp: string | null
  kenteken: string | null
  kentekenKleur: KentekenKleur | null
  keuze: BijtellingKeuzeType | null
  bijtellingVorigJaar: boolean | null
  gewijzigd: boolean | null
  kmRegistratieAkkoord: boolean | null
  heeftHandtekening: boolean
  bevestigdDoorHr: boolean
  beoordeeldOp: string | null
  beoordelingOpmerking: string | null
  vervallenReden: string | null
  /** Opvolging na goedkeuring. */
  opvolging: {
    verklaringId: string | null
    documentId: string | null
    nawMailVerstuurdOp: string | null
    nawBevestigdOp: string | null
    belastingdienstIngediendOp: string | null
    /**
     * Is de indiening van de keuze daarna mogelijk ingetrokken: een taak van een vervanging vroeg
     * erom en is doorgegeven (`GEVRAAGD`), of de stap is afgevinkt (`INGETROKKEN`).
     */
    belastingdienstIntrekking?: 'GEVRAAGD' | 'INGETROKKEN' | null
    contractMutatieId: string | null
    openTaken: number
    /**
     * De taken van de keuze, en (`naVervanging`) die van een verklaring die haar daarna verving.
     * `vervallenOmdat`: waarom de campagne een geannuleerde taak liet vervallen (alleen dan gevuld).
     */
    taken: Array<{
      id: string
      titel: string
      status: string
      autoRegelKey: string | null
      vervallenOmdat?: string | null
      naVervanging?: boolean
    }>
    /**
     * Wat er na het goedkeuren nog ontbreekt (leeg = compleet). Het scherm toont dit en biedt
     * "opvolging opnieuw uitvoeren"; zolang hier iets staat wordt de deelname niet "opgevolgd".
     */
    onvolledig: string[]
    /**
     * Voor het jaar geldt inmiddels een ANDERE verklaring dan de keuze uit de campagne (wel of
     * geen bijtelling, of een andere auto), vastgelegd via het voertuig. De rij toont de keuze
     * uit de campagne; dit zegt wat er nu geldt. `null` = de keuze uit de campagne geldt nog.
     */
    verklaringVervangen: BijtellingVerklaringVervangenDTO | null
    /**
     * De ingangsdatum (ISO) van de eerste latere contractregel die de verklaring van nu tegenspreekt
     * (een andere bijtelling, of bij "wel" een andere auto). Afgeleid uit de contracttijdlijn, niet
     * bewaard. `null` = de tijdlijn volgt de verklaring.
     */
    afwijkendeContractregel: string | null
  } | null
}

/** Wat er nu geldt als de keuze uit de campagne na de campagne is vervangen. */
export interface BijtellingVerklaringVervangenDTO {
  /** Wanneer de verklaring die nu geldt is vastgelegd (ISO). */
  op: string
  bijtelling: boolean
  kenteken: string | null
}

/** Uitkomst van goedkeuren, afkeuren of opnieuw opvolgen: de status plus wat aandacht vraagt. */
export interface BijtellingBeoordeelResultaatDTO {
  status: CampagneDeelnameStatus
  /** Onderdelen van de opvolging die niet lukten; het scherm toont ze, ze staan ook bij de deelname. */
  waarschuwingen: string[]
  /**
   * Ingangsdatum (ISO) van een latere contractregel die de keuze tegenspreekt; de melding staat ook
   * in `waarschuwingen`. Apart zodat bulk goedkeuren het kan tellen.
   */
  afwijkendeContractregel?: string | null
}

/**
 * Wat ontbreekt er aan de opvolging van een goedgekeurde keuze? Een pure toets op
 * de vastgelegde feiten, gedeeld door het scherm (tonen), de actie "opnieuw
 * uitvoeren" en de overgang naar OPGEVOLGD (die pas mag als hier niets staat).
 */
export function bijtellingOpvolgingOntbreekt(p: {
  keuze: BijtellingKeuzeType
  gewijzigdTovVorigJaar: boolean
  verklaringId: string | null
  contractMutatieId: string | null
  documentId: string | null
  /** De `autoRegelKey` van elke bestaande (niet-verwijderde) opvolgtaak. */
  taakKeys: ReadonlyArray<string | null>
  /** De `autoRegelKey` van de opvolgtaken die nog openstaan (niet uitgevoerd of geannuleerd). */
  openTaakKeys?: ReadonlyArray<string | null>
  /** Wanneer de correctiemail naar de salarisverwerker is verstuurd; null = niet. */
  nawMailVerstuurdOp?: string | Date | null
  /**
   * De verklaring uit de campagne is daarna vervangen door een verklaring met een andere uitkomst
   * of auto. De opvolging van de oude keuze loopt dan niet meer (die zou iets doorgeven dat niet
   * meer klopt); de taak voor de salarisverwerker hoort bij de nieuwe verklaring.
   */
  vervangen?: boolean
  /**
   * Na een vervanging staat er nog een taak van de oude keuze open die had moeten vervallen (de
   * opvolging van de vervanging liep mis): "opnieuw uitvoeren" haalt dat in.
   */
  vervangingOnaf?: boolean
}): string[] {
  if (p.vervangen) {
    return p.vervangingOnaf
      ? [
          'De opvolging van de vervanging is niet afgerond: een taak van de oude keuze voor de Belastingdienst of de autowissel staat nog open. Voer de opvolging opnieuw uit.',
        ]
      : []
  }
  const ontbreekt: string[] = []
  if (!p.verklaringId) ontbreekt.push('De gebruiksverklaring is niet vastgelegd.')
  if (!p.contractMutatieId) {
    ontbreekt.push('De bijtelling staat niet op de contracttijdlijn: er is geen contractregel op de ingangsdatum.')
  }
  if (p.keuze !== 'BIJTELLING') {
    if (!p.documentId) ontbreekt.push('De verklaring staat nog niet als document in het dossier.')
    if (!p.taakKeys.includes('BIJTELLING_VERKLARING')) ontbreekt.push('De taak voor de indiening bij de Belastingdienst ontbreekt.')
  }
  if (p.gewijzigdTovVorigJaar && !p.taakKeys.includes('BIJTELLING_NAW')) {
    ontbreekt.push('De taak voor de correctie naar de salarisverwerker ontbreekt.')
  }
  // De mail is niet verstuurd (geen adres ingesteld, tegengehouden of mislukt) en de taak staat
  // nog open: dan is "opnieuw uitvoeren" de weg om hem alsnog te sturen. Is de taak afgehandeld
  // (de correctie is met de hand gedaan en bevestigd), dan ontbreekt er niets meer.
  if (p.gewijzigdTovVorigJaar && !p.nawMailVerstuurdOp && (p.openTaakKeys ?? []).includes('BIJTELLING_NAW')) {
    ontbreekt.push(
      'De mail naar de salarisverwerker is niet verstuurd. Voer de opvolging opnieuw uit, of verstuur de correctie zelf en vink de bevestiging af.',
    )
  }
  return ontbreekt
}

/** De eigen stand per lopende bijtellingscampagne, compact: de kaart op Mijn voertuig. */
export interface MijnBijtellingCampagneKaartDTO {
  campagneId: string
  jaar: number
  naam: string
  deelnameStatus: CampagneDeelnameStatus
  /** Eigen deadline (ISO). */
  deadline: string
  dagenResterend: number
  keuze: BijtellingKeuzeType | null
  magIndienen: boolean
  /** Herkansing na een afkeuring: indienen kan tot deze datum (ISO), anders null. */
  herkansingTot: string | null
  /** De reden van een afkeuring, of null. */
  afkeurReden: string | null
}

/**
 * Meta bij `GET /campagnes/bijtelling/mijn`. Loopt de eigen verklaring van het
 * lopende jaar via een bijtellingscampagne, dan is de kaart "Verklaring
 * auto-gebruik" (FO-113) geen invoerweg meer: de keuze gaat langs de beoordeling.
 */
export interface MijnBijtellingCampagnesMetaDTO {
  verklaringViaCampagne: { campagneId: string; jaar: number; naam: string } | null
}

/**
 * Wat de ingelogde gebruiker in een bijtellingscampagne mag (FO-501 §4). De API
 * bepaalt dit; de schermen tonen op basis hiervan knoppen en leiden de regel
 * niet zelf opnieuw af.
 */
export interface BijtellingCampagneRechtenDTO {
  /** De campagne met de keuzes van collega's inzien. */
  inzien: boolean
  /** Goedkeuren, afkeuren, bulk goedkeuren en bevestigen na de deadline. */
  beoordelen: boolean
  /** Opvolgstappen afvinken en achterblijvers herinneren. */
  opvolgen: boolean
  /** De campagne aanmaken, wijzigen, openen en sluiten (HR en beheer). */
  inrichten: boolean
}

export interface BijtellingCampagneTellersDTO {
  doelgroep: number
  nietGestart: number
  ingediend: number
  goedgekeurd: number
  afgewezen: number
  opgevolgd: number
  vervallen: number
  /**
   * Hoeveel ingediende keuzes "ongewijzigd" zijn en dus in bulk goed te keuren, over de HELE
   * campagne. De knop toont dit getal: wat er staat is wat de server goedkeurt.
   */
  bulkGoedkeurbaar: number
  /** Ingediend/beoordeeld/opgevolgd per BV (entity). */
  perEntity: Array<{
    entityId: string | null
    entityNaam: string
    doelgroep: number
    ingediend: number
    beoordeeld: number
    opgevolgd: number
  }>
}

export interface BijtellingCampagneDetailDTO {
  id: string
  naam: string
  jaar: number
  status: 'CONCEPT' | 'OPEN' | 'GESLOTEN' | 'AFGEROND'
  startDatum: string
  deadline: string
  entityIds: string[]
  departmentIds: string[]
  geldigVanaf: string
  geldigTot: string
  /** Aantal dagen dat een medewerker na een afkeuring opnieuw kan indienen. */
  herkansingDagen: number
  tellers: BijtellingCampagneTellersDTO
}

export interface BijtellingBulkGoedkeurenResultaatDTO {
  goedgekeurd: number
  overgeslagen: number
}
